/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Icon } from 'semantic-ui-react'
import { subDays, startOfDay, formatISO } from 'date-fns'
import { Link } from 'react-router-dom-v5-compat'
import { Card, CardContent, CardExtra } from '../../Shared/Card'
import { useOpeningTimes, useOverallOpeningTimes } from '../../../Home/OpeningTimes/hooks'
import { isProviderACompany, isProviderAPos } from '../../../../services/Provider/Provider.service'
import useUser from '../../../Shared/UserProvider/useUser'
import { getColor } from '../../../Home/OpeningTimes/openingTimeHelpers'
import { LoadingPlaceholderCardContent } from '../../Shared/LoadingPlaceholderCardContent'
import { useTranslation } from 'react-i18next'
import { RateRadialChart } from '../../../Shared/RateRadialChart'
import { CardTitle } from '../../Shared/CardTitle'
import React from 'react'

const OpeningCard = () => {
    const [t] = useTranslation()

    const {user} = useUser()
    const from = formatISO(startOfDay(subDays(new Date(), 7)))
    const to = formatISO(startOfDay(new Date()))

    const isCompanyOrOwnScope = isProviderACompany(user.provider) || user.isLoggedInOwnScope
    const {data: overallOpeningTimes} = useOverallOpeningTimes({
        from,
        to,
        enabled: isCompanyOrOwnScope,
    })

    const {data: posOpeningTimes} = useOpeningTimes({
        from,
        to,
        page: 1,
        size: 1,
        posIds: isProviderAPos(user.provider) ? [user.pos?.id] : [],
        enabled: isProviderAPos(user.provider),
    })

    const openingRate = isCompanyOrOwnScope ?
      overallOpeningTimes?.averageOpenRate :
      posOpeningTimes && posOpeningTimes.length > 0 ?
        posOpeningTimes[0].averageOpenRate :
        null

    const getBorderColor = () => {
        if (!openingRate) {
            return 'unset'
        }

        return getColor(openingRate)
    }

    return (
      <Card
        css={css`
            border-top: 2px solid ${getBorderColor(openingRate)};

            .card-content {
                width: 280px;
                height: 175px;
            }
        `}
      >
          {openingRate ? (
            <CardContent>
                <CardTitle
                  css={css`
                      margin-top: .25rem;
                  `}
                  title={t('dashboard.opening.title')}
                />
                <RateRadialChart
                  label={t('dashboard.opening.label')}
                  rate={openingRate}
                  animationsEnabled
                  getColor={getColor}
                  css={css`
                      margin-top: 1.5rem;
                  `}
                />
            </CardContent>
          ) : (
            <LoadingPlaceholderCardContent/>
          )}
          <CardExtra>
              <Link
                to={{
                    pathname: '/analytics/openings/opening-times',
                    search: new URLSearchParams({
                        from,
                        to,
                    }).toString(),
                }}>
                  {t('dashboard.opening.extra_link')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { OpeningCard }
